import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Eye, Edit, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileText, BookOpen } from 'lucide-react';
import { apiClient } from '../config/api';
import PriceSparkline, { SparklineProvider } from './PriceSparkline';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    company: '',
    activist: '',
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [pageSize] = useState(20); // Show 20 reports per page
  const searchTimeoutRef = useRef(null);
  const filtersRef = useRef(filters);

  // Keep filters ref in sync
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchReports = useCallback(async (customFilters = null, customPage = null, tab = null) => {
    try {
      setLoading(true);
      const currentTab = tab || activeTab;
      const params = {
        page: customPage || currentPage,
        limit: pageSize,
        sort: 'desc',
        ...(currentTab === 'research' ? { type: 'research' } : {}),
        ...(customFilters || filtersRef.current)
      };

      const response = await apiClient.get('/api/short-sellers', { params });
      
      // Map API response fields to frontend expected fields
      const mappedReports = response.data.map(report => ({
        id: report.id,
        report_title: report.report_title,
        target_company: report.company_name, // API returns company_name
        ticker: report.company_ticker, // API returns company_ticker
        short_seller: report.activist_short_seller, // API returns activist_short_seller
        publication_date: report.report_date, // API returns report_date
        link: report.report_url, // API returns report_url
        sector: report.sector,
        last_update: report.last_update
      }));
      
      setReports(mappedReports);
      
      // Calculate total pages based on total count
      // For now, we'll estimate based on current data length
      // In a real implementation, the API should return total count
      const estimatedTotal = response.data.length === pageSize ? 
        (customPage || currentPage) * pageSize + 1 : 
        (customPage || currentPage) * pageSize;
      
      setTotalReports(estimatedTotal);
      setTotalPages(Math.ceil(estimatedTotal / pageSize));
    } catch (err) {
      setError('Failed to fetch reports');
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  // Initial load, page changes, and tab changes
  useEffect(() => {
    fetchReports(null, null, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setFilters({ company: '', activist: '', startDate: '', endDate: '' });
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // For text inputs, use debounced search
    if (field === 'company' || field === 'activist') {
      searchTimeoutRef.current = setTimeout(() => {
        fetchReports(newFilters, 1);
      }, 500);
    } else {
      // For date inputs, search immediately
      fetchReports(newFilters, 1);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        await apiClient.delete(`/api/short-sellers/${id}`);
        fetchReports(); // Refresh the list
      } catch (err) {
        console.error('Error deleting report:', err);
        alert('Failed to delete report');
      }
    }
  };

  const clearFilters = () => {
    const emptyFilters = {
      company: '',
      activist: '',
      startDate: '',
      endDate: ''
    };
    setFilters(emptyFilters);
    setCurrentPage(1);
    
    // Clear timeout and fetch immediately
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    fetchReports(emptyFilters, 1);
  };

  const applyFilters = () => {
    // Clear timeout and fetch immediately
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    fetchReports(filters, 1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      const start = Math.max(1, currentPage - 3);
      const end = Math.min(totalPages, currentPage + 3);
      
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    // One sparkline request for the whole page, keyed to the ids actually on
    // screen — a per-row fetch would make this table N+1.
    <SparklineProvider reportIds={reports.map((r) => r.id).filter(Boolean)}>
    <div className="space-y-6 p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Short Seller Reports</h1>
        <button className="btn-primary">Add New Report</button>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => handleTabChange('reports')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'reports'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FileText size={14} />
          Company Reports
        </button>
        <button
          onClick={() => handleTabChange('research')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'research'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpen size={14} />
          Research &amp; Commentary
          <span className="ml-1 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">235</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-4 mb-4">
          <Search className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Search & Filter</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              value={filters.company}
              onChange={(e) => handleFilterChange('company', e.target.value)}
              placeholder="Search by company..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Short Seller
            </label>
            <input
              type="text"
              value={filters.activist}
              onChange={(e) => handleFilterChange('activist', e.target.value)}
              placeholder="Search by activist..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Clear Filters
          </button>
          <button
            onClick={applyFilters}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex justify-between items-center text-sm text-gray-600">
        <div>
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalReports)} of {totalReports} reports
        </div>
        <div>
          Page {currentPage} of {totalPages}
        </div>
      </div>

      {/* Reports Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 shadow-sm border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                  {activeTab === 'research' ? 'Title' : 'Report Title'}
                </th>
                {activeTab === 'reports' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Company
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Short Seller
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Date
                </th>
                {activeTab === 'reports' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Sector
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  90d impact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr 
                  key={report.id} 
                  className="hover:bg-gray-50 cursor-pointer" 
                  onClick={() => {
                    const activist = encodeURIComponent(report.short_seller || '');
                    const slug = encodeURIComponent((report.report_title || '').trim());
                    const date = report.publication_date ? new Date(report.publication_date).toISOString().slice(0,10) : '';
                    const ticker = (report.ticker || '').trim();
                    if (activist && slug) {
                      const q = new URLSearchParams();
                      if (date) q.set('date', date);
                      if (ticker) q.set('ticker', ticker);
                      const qs = q.toString();
                      window.open(`/report/${activist}/${slug}${qs ? `?${qs}` : ''}`, '_blank');
                    }
                  }}
                >
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900 line-clamp-2">
                      {report.report_title || 'N/A'}
                    </div>
                  </td>
                  {activeTab === 'reports' && (
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {report.target_company || 'N/A'}
                      </div>
                      <div className="text-xs font-mono text-gray-500">
                        {report.ticker || ''}
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {report.short_seller || 'N/A'}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {report.publication_date ?
                        new Date(report.publication_date).toLocaleDateString() :
                        'N/A'
                      }
                    </div>
                  </td>
                  {activeTab === 'reports' && (
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {report.sector || 'N/A'}
                      </div>
                    </td>
                  )}
                  {/* Shape of the 90 days after publication. Down is the short
                      working, so green here means the thesis paid. */}
                  <td className="px-4 py-4">
                    <PriceSparkline reportId={report.id} />
                  </td>
                  <td className="px-4 py-4 text-sm font-medium">
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(report.link, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="View Report"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle edit
                        }}
                        className="text-green-600 hover:text-green-900 p-1"
                        title="Edit Report"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(report.id);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete Report"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex items-center space-x-1">
              {getPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' ? goToPage(page) : null}
                  disabled={page === '...'}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : page === '...'
                      ? 'text-gray-400 cursor-default'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </SparklineProvider>
  );
};

export default Reports;
